import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useCreateUser, useOrganizations, useUpdateUser, useUser } from '@/hooks/use-admin'
import type { AdminUserCreate, AdminUserUpdate } from '@/services/admin.service'
import { ArrowLeft, Loader2 } from 'lucide-react'
import type React from 'react'
import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

export function AdminUserFormPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isEditing = !!id

  const { data: user, isLoading: isLoadingUser } = useUser(Number(id))
  const { data: organizationsData, isLoading: isLoadingOrgs } = useOrganizations({ limit: 100 })
  const createUser = useCreateUser()
  const updateUser = useUpdateUser()

  const organizations = organizationsData?.organizations ?? []

  const [formData, setFormData] = useState<
    Omit<AdminUserCreate & AdminUserUpdate, 'organization_id'>
  >({
    email: '',
    phone_number: '',
    first_name: '',
    last_name: '',
    password: '',
    auth_method: 'email',
    is_active: true,
    is_superuser: false,
    is_hr_admin: false,
  })
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null)

  useEffect(() => {
    if (user) {
      const userOrganizationId = user.organization_id ?? user.organization?.id ?? null
      setFormData({
        email: user.email || '',
        phone_number: user.phone_number || '',
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        password: '',
        auth_method: user.auth_method ?? 'email',
        is_active: user.is_active,
        is_superuser: user.is_superuser,
        is_hr_admin: user.is_hr_admin ?? false,
      })
      setSelectedOrgId(userOrganizationId)
    }
  }, [user])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Clean up empty strings and add organization_id
    const cleanData = Object.fromEntries(
      Object.entries(formData).filter(([_, v]) => v !== ''),
    ) as AdminUserCreate & AdminUserUpdate

    // Always include organization_id when editing (0 means "no organization", backend handles it)
    // For create, only include if a real organization is selected
    if (isEditing) {
      cleanData.organization_id = selectedOrgId ?? 0
    } else if (selectedOrgId !== null) {
      cleanData.organization_id = selectedOrgId
    }

    try {
      if (isEditing) {
        await updateUser.mutateAsync({ id: Number(id), data: cleanData })
      } else {
        await createUser.mutateAsync(cleanData)
      }
      navigate('/users')
    } catch (error) {
      console.error('Failed to save user:', error)
    }
  }

  const isSubmitting = createUser.isPending || updateUser.isPending

  // Wait for both user and organizations to load when editing
  // This ensures the Select component has both value and options available
  if ((isEditing && isLoadingUser) || isLoadingOrgs) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/users">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Link>
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {isEditing ? 'Edit User' : 'Create User'}
          </h1>
          <p className="text-muted-foreground">
            {isEditing ? 'Update user information' : 'Add a new user to the platform'}
          </p>
        </div>
      </div>

      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle>User Details</CardTitle>
          <CardDescription>Enter the user's information below</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="first_name">First Name</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  placeholder="John"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Last Name</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  placeholder="Doe"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="john@example.com"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone_number">Phone Number</Label>
              <Input
                id="phone_number"
                type="tel"
                value={formData.phone_number}
                onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                placeholder="+1234567890"
              />
            </div>

            {!isEditing && (
              <div className="space-y-2">
                <Label htmlFor="password">Password (optional)</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Leave blank for SMS-only auth"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>Auth Method</Label>
              <div className="flex gap-4">
                {(['email', 'sms', 'both'] as const).map((method) => (
                  <label key={method} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="auth_method"
                      value={method}
                      checked={formData.auth_method === method}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          auth_method: e.target.value as 'email' | 'sms' | 'both',
                        })
                      }
                      className="h-4 w-4"
                    />
                    <span className="text-sm capitalize">{method}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="organization">Organization</Label>
              <Select
                value={selectedOrgId?.toString() ?? 'none'}
                onValueChange={(value: string) =>
                  setSelectedOrgId(value === 'none' ? null : Number(value))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select an organization" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No organization</SelectItem>
                  {organizations.map((org) => (
                    <SelectItem key={org.id} value={org.id.toString()}>
                      {org.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-6">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="is_active"
                  checked={formData.is_active ?? false}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, is_active: checked === true })
                  }
                />
                <Label htmlFor="is_active" className="cursor-pointer">
                  Active
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="is_hr_admin"
                  checked={formData.is_hr_admin ?? false}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, is_hr_admin: checked === true })
                  }
                />
                <Label htmlFor="is_hr_admin" className="cursor-pointer">
                  HR Admin
                </Label>
              </div>
            </div>

            <div className="flex gap-4">
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {isEditing ? 'Save Changes' : 'Create User'}
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate('/users')}>
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
